'use client';

import { useState, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { ReportTabs, type ReportTabView } from './ReportTabs';
import { CompareView } from './CompareView';
import { ActionPlan } from './ActionPlan';
import { QuickWins } from './QuickWins';
import { CategorySection } from './CategorySection';
import { BreakEvenBlock } from './ProReport/BreakEvenBlock';
import { SiteHealthBlock } from './ProReport/SiteHealthBlock';
import { computeBreakEven } from '@/lib/audit/break-even';
import {
  VisualHero,
  FallbackHeader,
  ExecutiveSummary,
  FindingsGrid,
  PrinciplesSummary,
  ReportCTA,
  ComparisonStats,
  ReportFooter,
} from './ProReport';
import type { Finding, ProReportData, SeverityDataItem, AuditFinding } from './ProReport/types';
import {
  normalizeSeverity,
  extractCategories,
  extractUniquePrinciples,
  deriveCategory,
} from '@/lib/audit/pro-report-utils';
import {
  compareDeviceFindings,
  type ComparisonResult,
} from '@/lib/audit/cross-device-comparison';
import { organizeFindings } from '@/lib/audit/report-organizer';
import styles from './ProReportResults.module.css';
import { downloadReportPdf } from '@/lib/audit/download-pdf';

interface ProReportResultsProps {
  data: ProReportData;
  locale?: string;
  /** Signed link token from the URL, presented to the PDF route (S-015). */
  accessToken?: string;
  /** The sentence a partial vision failure gets, already localised; null when both devices completed. */
  partialFailure?: string | null;
  /** Lines for checks that produced no verdict, already localised. */
  notAssessedLines?: string[];
}

export function ProReportResults({ data, locale = 'en', accessToken, partialFailure = null, notAssessedLines = [] }: ProReportResultsProps) {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set([0, 1, 2]));
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [deviceView, setDeviceView] = useState<ReportTabView>('desktop');
  const findingRefs = useRef<Map<string, HTMLElement>>(new Map());

  const { reportId, url, timestamp, screenshotUrl, summary, desktop, mobile } = data;

  const backText = locale === 'es' ? '← Volver al panel' : '← Back to Dashboard';

  const hasDesktop = !!desktop || !!screenshotUrl;
  const hasMobile = !!mobile;

  // Get findings for current view
  const findings = useMemo(() => {
    if (deviceView === 'compare') {
      const allFindings: Finding[] = [];
      if (desktop?.findings) {
        allFindings.push(...desktop.findings.map((f) => ({ ...f, device: 'desktop' as const })));
      }
      if (mobile?.findings) {
        allFindings.push(...mobile.findings.map((f) => ({ ...f, device: 'mobile' as const })));
      }
      if (allFindings.length === 0 && data.findings) {
        return data.findings;
      }
      return allFindings;
    }
    if (deviceView === 'mobile' && mobile?.findings) {
      return mobile.findings;
    }
    if (deviceView === 'desktop' && desktop?.findings) {
      return desktop.findings;
    }
    return data.findings;
  }, [deviceView, desktop, mobile, data.findings]);

  // Get screenshot for current view
  const currentScreenshot = useMemo(() => {
    if (deviceView === 'mobile' && mobile?.screenshotUrl) {
      return mobile.screenshotUrl;
    }
    if (deviceView === 'desktop' && desktop?.screenshotUrl) {
      return desktop.screenshotUrl;
    }
    return screenshotUrl;
  }, [deviceView, desktop, mobile, screenshotUrl]);

  // Get summary for current view
  const currentSummary = useMemo(() => {
    if (deviceView === 'mobile' && mobile?.summary) {
      return mobile.summary;
    }
    if (deviceView === 'desktop' && desktop?.summary) {
      return desktop.summary;
    }
    return summary;
  }, [deviceView, desktop, mobile, summary]);

  // Cross-device comparison
  const comparison = useMemo((): ComparisonResult | null => {
    if (!desktop?.findings || !mobile?.findings) return null;

    const toAuditFinding = (f: Finding, device: 'desktop' | 'mobile'): AuditFinding => ({
      principleId: f.principleId || f.id || 'UNKNOWN',
      principleTitle: f.principleTitle || f.title || f.help || 'UX Issue',
      severity: normalizeSeverity(f.severity),
      description: f.description,
      recommendation: f.recommendation || '',
      affectedElements: f.affectedElements || [],
      device,
    });

    return compareDeviceFindings({
      desktop: desktop.findings.map((f) => toAuditFinding(f, 'desktop')),
      mobile: mobile.findings.map((f) => toAuditFinding(f, 'mobile')),
    });
  }, [desktop, mobile]);

  // Convert findings for CompareView
  const desktopAuditFindings = useMemo((): AuditFinding[] => {
    if (!desktop?.findings) return [];
    return desktop.findings.map((f) => ({
      principleId: f.principleId || f.id || 'UNKNOWN',
      principleTitle: f.principleTitle || f.title || f.help || 'UX Issue',
      severity: normalizeSeverity(f.severity),
      description: f.description,
      recommendation: f.recommendation || '',
      affectedElements: f.affectedElements || [],
      device: 'desktop' as const,
    }));
  }, [desktop]);

  const mobileAuditFindings = useMemo((): AuditFinding[] => {
    if (!mobile?.findings) return [];
    return mobile.findings.map((f) => ({
      principleId: f.principleId || f.id || 'UNKNOWN',
      principleTitle: f.principleTitle || f.title || f.help || 'UX Issue',
      severity: normalizeSeverity(f.severity),
      description: f.description,
      recommendation: f.recommendation || '',
      affectedElements: f.affectedElements || [],
      device: 'mobile' as const,
    }));
  }, [mobile]);

  // B-05: the break-even from the inputs the client gave at checkout. Pure
  // arithmetic over their numbers; see lib/audit/break-even.ts.
  const breakEven = useMemo(
    () =>
      computeBreakEven(
        {
          successAction: data.successAction ?? null,
          conversionValueCents: data.conversionValueCents ?? null,
          monthlySessions: data.monthlySessions ?? null,
          actionRate: data.actionRate ?? null,
        },
        (data.findings || []).map((f) => ({
          principleId: f.principleId || f.id || 'UNKNOWN',
          principleTitle: f.principleTitle || f.title || 'UX Issue',
          severity: normalizeSeverity(f.severity),
          description: f.description,
          recommendation: f.recommendation || '',
          effortEstimate: f.effortEstimate,
          onActionPath: f.onActionPath,
        }))
      ),
    [data]
  );

  // Computed values
  const categories = useMemo(() => extractCategories(findings), [findings]);
  const principles = useMemo(() => extractUniquePrinciples(findings), [findings]);

  const filteredFindings = useMemo(() => {
    if (!activeFilter) return findings;
    return findings.filter((f) => normalizeSeverity(f.severity) === activeFilter);
  }, [findings, activeFilter]);

  // Organize findings for ActionPlan/QuickWins
  const organizedReport = useMemo(() => {
    const auditFindings: AuditFinding[] = findings.map((f) => ({
      principleId: f.principleId || f.id || 'UNKNOWN',
      principleTitle: f.principleTitle || f.title || f.help || 'UX Issue',
      severity: normalizeSeverity(f.severity),
      description: f.description,
      recommendation: f.recommendation || '',
      affectedElements: f.affectedElements || [],
      device: f.device === 'both' ? undefined : f.device,
      auditCode: f.auditCode,
      auditCategoryLabel: f.auditCategoryLabel,
    }));
    return organizeFindings(auditFindings);
  }, [findings]);

  // Group findings by category
  const findingsByCategory = useMemo(() => {
    const grouped = new Map<string, AuditFinding[]>();

    // Group by the native Audit Criteria category when the finding resolved to
    // one (B-02, 2026-09-04), so the sections here match the tags on each card
    // and the PDF. The keyword buckets in deriveCategory remain only for findings
    // that resolved to nothing.
    const categoryIds = new Map<string, string | undefined>();
    findings.forEach((f) => {
      const category = f.auditCategoryLabel || deriveCategory(f);
      if (!grouped.has(category)) {
        grouped.set(category, []);
        categoryIds.set(category, f.auditCategoryLabel ? f.auditCategory : undefined);
      }
      grouped.get(category)!.push({
        principleId: f.principleId || f.id || 'UNKNOWN',
        principleTitle: f.principleTitle || f.title || f.help || 'UX Issue',
        severity: normalizeSeverity(f.severity),
        description: f.description,
        recommendation: f.recommendation || '',
        affectedElements: f.affectedElements || [],
        device: f.device === 'both' ? undefined : f.device,
        auditCode: f.auditCode,
        auditCategoryLabel: f.auditCategoryLabel,
      });
    });

    return Array.from(grouped.entries())
      .map(([category, categoryFindings]) => ({
        category,
        categoryId: categoryIds.get(category),
        findings: categoryFindings.sort((a, b) => {
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          return severityOrder[a.severity] - severityOrder[b.severity];
        }),
      }))
      .sort((a, b) => b.findings.length - a.findings.length);
  }, [findings]);

  // Severity data for charts
  const total = currentSummary.totalIssues || findings.length;
  const severityData: SeverityDataItem[] = [
    { label: 'Critical', count: currentSummary.criticalIssues, color: 'var(--critical)' },
    { label: 'High', count: currentSummary.highIssues, color: 'var(--severity-high)' },
    { label: 'Medium', count: currentSummary.mediumIssues, color: 'var(--severity-medium)' },
    { label: 'Low', count: currentSummary.lowIssues, color: 'var(--severity-low)' },
  ];

  // Handlers
  const handleCompareViewFindingClick = useCallback(
    (finding: AuditFinding, device: 'desktop' | 'mobile') => {
      setDeviceView(device);
      setTimeout(() => {
        const index = findings.findIndex(
          (f) =>
            (f.principleId || f.id) === finding.principleId ||
            (f.principleTitle || f.title || f.help) === finding.principleTitle
        );

        if (index !== -1) {
          setExpandedCards((prev) => new Set(prev).add(index));
          setTimeout(() => {
            const ref = findingRefs.current.get(String(index));
            if (ref) {
              ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
              ref.classList.add(styles.findingHighlight);
              setTimeout(() => ref.classList.remove(styles.findingHighlight), 2000);
            }
          }, 100);
        }
      }, 100);
    },
    [findings]
  );

  const handleCategoryFindingClick = useCallback(
    (finding: AuditFinding) => {
      const index = findings.findIndex(
        (f) =>
          (f.principleId || f.id) === finding.principleId ||
          (f.principleTitle || f.title || f.help) === finding.principleTitle
      );

      if (index !== -1) {
        setExpandedCards((prev) => new Set(prev).add(index));
        setTimeout(() => {
          const ref = findingRefs.current.get(String(index));
          if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
            ref.classList.add(styles.findingHighlight);
            setTimeout(() => ref.classList.remove(styles.findingHighlight), 2000);
          }
        }, 100);
      }
    },
    [findings]
  );

  const handleFindingClick = useCallback(
    (finding: { principleId: string; principleTitle: string }) => {
      const index = findings.findIndex(
        (f) =>
          (f.principleId || f.id) === finding.principleId ||
          (f.principleTitle || f.title || f.help) === finding.principleTitle
      );

      if (index !== -1) {
        setExpandedCards((prev) => new Set(prev).add(index));
        setTimeout(() => {
          const ref = findingRefs.current.get(String(index));
          if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
            ref.classList.add(styles.findingHighlight);
            setTimeout(() => ref.classList.remove(styles.findingHighlight), 2000);
          }
        }, 100);
      }
    },
    [findings]
  );

  const toggleCard = useCallback((index: number) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleDownloadPDF = useCallback(async () => {
    try {
      await downloadReportPdf(reportId, { token: accessToken });
    } catch (error) {
      console.error('PDF download error:', error);
    }
  }, [reportId, accessToken]);

  const handleBookCall = useCallback(() => {
    // Paid session: route through the sales page + Polar checkout (the old
    // direct TidyCal link let anyone book without paying)
    window.open(`/${locale}/discovery-call`, '_blank');
  }, [locale]);

  return (
    <div className={styles.container}>
      {/* Back Navigation */}
      <Link href={`/${locale}/dashboard`} className={styles.backLink}>
        <ArrowLeft size={16} />
        {backText}
      </Link>

      {/* Ambient Background Effects */}
      <div className={styles.ambientGlow} />

      {/* Visual Hero Section */}
      {currentScreenshot ? (
        <VisualHero
          screenshotUrl={currentScreenshot}
          url={url}
          timestamp={timestamp}
          summary={currentSummary}
          total={total}
          severityData={severityData}
          deviceView={deviceView}
          locale={locale}
          onDownloadPDF={handleDownloadPDF}
        />
      ) : (
        <FallbackHeader
          url={url}
          timestamp={timestamp}
          locale={locale}
          onDownloadPDF={handleDownloadPDF}
        />
      )}

      {/* Report View Tabs */}
      {(hasDesktop || hasMobile) && (
        <ReportTabs
          activeTab={deviceView}
          onTabChange={setDeviceView}
          hasDesktop={hasDesktop}
          hasMobile={hasMobile}
          desktopCount={desktop?.summary?.totalIssues}
          mobileCount={mobile?.summary?.totalIssues}
          locale={locale}
        />
      )}

      {/* Compare View */}
      {deviceView === 'compare' && hasDesktop && hasMobile && (
        <>
          <section className={styles.compareViewSection}>
            <CompareView
              desktopFindings={desktopAuditFindings}
              mobileFindings={mobileAuditFindings}
              desktopScreenshot={desktop?.screenshotUrl}
              mobileScreenshot={mobile?.screenshotUrl}
              locale={locale}
              onFindingClick={handleCompareViewFindingClick}
            />
          </section>

          {comparison && <ComparisonStats comparison={comparison} locale={locale} />}
        </>
      )}

      {/* Desktop/Mobile View Content */}
      {deviceView !== 'compare' && (
        <>
          <BreakEvenBlock breakEven={breakEven} locale={locale} />
          {(partialFailure || notAssessedLines.length > 0) && (
            <section className={styles.methodNote} aria-label={locale === 'es' ? 'Alcance del análisis' : 'Analysis scope'}>
              {partialFailure && <p>{partialFailure}</p>}
              {notAssessedLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </section>
          )}
          {data.siteHealth ? <SiteHealthBlock health={data.siteHealth} locale={locale} /> : null}
          <ExecutiveSummary
            total={total}
            severityData={severityData}
            categories={categories}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />

          {/* Action Plan */}
          {organizedReport.actionPlan.length > 0 && (
            <section className={styles.actionPlanSection}>
              <ActionPlan
                findings={organizedReport.actionPlan}
                locale={locale}
                onFindingClick={handleFindingClick}
              />
            </section>
          )}

          {/* Quick Wins */}
          {organizedReport.quickWins.length > 0 && (
            <section className={styles.quickWinsSection}>
              <QuickWins
                findings={organizedReport.quickWins}
                locale={locale}
                onFindingClick={handleFindingClick}
              />
            </section>
          )}

          {/* Category Analysis */}
          {findingsByCategory.length > 0 && (
            <section className={styles.categoryAnalysisSection}>
              <div className={styles.categoryAnalysisHeader}>
                <span className={styles.sectionLabel}>
                  {locale === 'es' ? 'ANALISIS POR CATEGORIA' : 'ANALYSIS BY CATEGORY'}
                </span>
                <h2 className={styles.sectionTitle}>
                  {locale === 'es' ? 'Hallazgos Detallados' : 'Detailed Findings'}
                </h2>
                <p className={styles.categoryAnalysisIntro}>
                  {locale === 'es'
                    ? 'Cada categoria agrupa problemas relacionados con recomendaciones especificas para su sitio.'
                    : 'Each category groups related issues with specific recommendations for your site.'}
                </p>
              </div>

              <div className={styles.categoryList}>
                {findingsByCategory.map(({ category, categoryId, findings: categoryFindings }, index) => (
                  <CategorySection
                    key={category}
                    category={category}
                    categoryId={categoryId}
                    findings={categoryFindings}
                    screenshotUrl={currentScreenshot}
                    locale={locale}
                    onFindingClick={handleCategoryFindingClick}
                    defaultExpanded={index < 2}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Findings Grid */}
          <FindingsGrid
            findings={findings}
            filteredFindings={filteredFindings}
            expandedCards={expandedCards}
            activeFilter={activeFilter}
            onToggleCard={toggleCard}
            onClearFilter={() => setActiveFilter(null)}
            findingRefs={findingRefs}
          />

          {/* Principles Summary */}
          <PrinciplesSummary principles={principles} />
        </>
      )}

      {/* CTA Section */}
      <ReportCTA onBookCall={handleBookCall} locale={locale} issueCount={total} />

      {/* Footer */}
      <ReportFooter reportId={reportId} />
    </div>
  );
}
